import {
  Badge,
  BadgeColor,
  Column,
  DropdownButtonCell,
  DropdownButtonCellComponentProps,
  MenuItemProps,
} from '@odin-labs/components';
import React from 'react';
import { Entry, Layout, sideFacingLabel } from 'siteLayout';

export type LayoutRowTag = { text: string; color: BadgeColor } | null;

export type EntryRow = {
  id: string;
  name: string;
  tag: LayoutRowTag;
  from: string;
  fromOutside: boolean;
  to: string;
  toOutside: boolean;
  /** Row actions, present only while editing the draft. */
  menuItems?: MenuItemProps[];
};

const classes = {
  name: 'odin-flex odin-items-center odin-gap-2',
  nameText: 'odin-font-medium odin-text-gray-900',
  outside: 'odin-text-gray-500',
};

/** A zone's or entry's name with its draft tag, for the first column. */
export function LayoutNameCell({
  row,
}: {
  row: { original: { name: string; tag: LayoutRowTag } };
}): React.ReactElement {
  return (
    <span className={classes.name}>
      <span className={classes.nameText}>{row.original.name || 'Unnamed'}</span>
      {row.original.tag && (
        <Badge text={row.original.tag.text} color={row.original.tag.color} />
      )}
    </span>
  );
}

function FromCell({
  row,
}: {
  row: { original: EntryRow };
}): React.ReactElement {
  return (
    <span className={row.original.fromOutside ? classes.outside : undefined}>
      {row.original.from}
    </span>
  );
}

function ToCell({ row }: { row: { original: EntryRow } }): React.ReactElement {
  return (
    <span className={row.original.toOutside ? classes.outside : undefined}>
      {row.original.to}
    </span>
  );
}

/** Name, then the zone on each side: the same columns wherever entries are listed. */
export const entryColumns: Column<EntryRow>[] = [
  { id: 'name', Header: 'Name', accessor: 'name', Cell: LayoutNameCell },
  { id: 'from', Header: 'From', accessor: 'from', Cell: FromCell },
  { id: 'to', Header: 'To', accessor: 'to', Cell: ToCell },
];

/** The entry columns with the row menu, for the list that can remove entries. */
export const entryColumnsWithActions: Column<EntryRow>[] = [
  ...entryColumns,
  {
    id: 'actions',
    Header: '',
    Cell: DropdownButtonCell,
    componentProps: (row: EntryRow): DropdownButtonCellComponentProps => ({
      cellAlignment: 'right',
      menuItems: row.menuItems ?? [],
    }),
  },
];

export const toEntryRows = (
  layout: Layout,
  entries: Entry[],
  tagOf: (entry: Entry) => LayoutRowTag,
  menuItemsOf: (entry: Entry) => MenuItemProps[] = () => [],
): EntryRow[] =>
  entries.map((entry) => ({
    id: entry.id,
    name: entry.name,
    tag: tagOf(entry),
    menuItems: menuItemsOf(entry),
    from: sideFacingLabel(layout, entry.sideA),
    fromOutside: entry.sideA.kind === 'OUTSIDE',
    to: sideFacingLabel(layout, entry.sideB),
    toOutside: entry.sideB.kind === 'OUTSIDE',
  }));
